import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import {
  formatBulanID,
  formatRupiahFull,
  formatRupiahSigned,
  formatTanggalID,
  currentBulan,
  shiftBulan,
} from "@/lib/format";
import { ChevronLeft, ChevronRight, Plus, Edit2, Trash2, ArrowLeftRight } from "lucide-react";
import TransaksiForm from "@/pages/forms/TransaksiForm";
import DeleteConfirm from "@/components/DeleteConfirm";
import { toast } from "sonner";

const FILTERS = [
  { v: "all", l: "Semua" },
  { v: "pemasukan", l: "Pemasukan" },
  { v: "pengeluaran", l: "Pengeluaran" },
  { v: "transfer", l: "Transfer" },
];

export default function Transaksi() {
  const [bulan, setBulan] = useState(currentBulan());
  const [tipe, setTipe] = useState("all");
  const [items, setItems] = useState([]);
  const [rekening, setRekening] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [search, setSearch] = useState("");
  const [searchMode, setSearchMode] = useState(false);
  const load = async () => {
    setLoading(true);
    try {
      const [t, r] = await Promise.all([
        search.trim()
          ? api.get(`/transaksi?search=${encodeURIComponent(search.trim())}&tipe=${tipe}`)
          : api.get(`/transaksi?bulan=${bulan}&tipe=${tipe}`),
        api.get("/rekening"),
      ]);
      setItems(t.data);
      setRekening(r.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulan, tipe, search]);

  const grouped = useMemo(() => {
    const g = items.reduce((acc, t) => {
      (acc[t.tanggal] = acc[t.tanggal] || []).push(t);
      return acc;
    }, {});
    return Object.keys(g)
      .sort((a, b) => b.localeCompare(a))
      .map((k) => {
        let masuk = 0,
          keluar = 0;
        for (const t of g[k]) {
          if (t.tipe === "pemasukan") masuk += t.nominal;
          else if (t.tipe === "pengeluaran") keluar += t.nominal;
          // transfer tidak dihitung di sub-total masuk/keluar
        }
        return { tanggal: k, items: g[k], masuk, keluar };
      });
  }, [items]);

  const doDelete = async () => {
    try {
      await api.delete(`/transaksi/${confirmDel.id}`);
      toast.success("Transaksi dihapus");
      setConfirmDel(null);
      load();
    } catch (e) {
      toast.error("Gagal menghapus");
    }
  };

  const isCurrent = bulan === currentBulan();

  return (
    <div className="pb-6" data-testid="transaksi-page">
      {/* Header + month nav */}
      <div
        className="px-4 pt-5 pb-3 sticky top-0 z-10"
        style={{ backgroundColor: "#f8fafc" }}
      >
        <div className="flex items-center justify-between">
          <button
            onClick={() => setBulan(shiftBulan(bulan, -1))}
            data-testid="bulan-prev"
            className="w-9 h-9 rounded-full bg-white border border-slate-200 flex items-center justify-center tap"
          >
            <ChevronLeft size={18} />
          </button>
          <div className="text-base font-extrabold text-slate-900" data-testid="bulan-label">
            {formatBulanID(bulan)}
          </div>
          <button
            disabled={isCurrent}
            onClick={() => setBulan(shiftBulan(bulan, 1))}
            data-testid="bulan-next"
            className="w-9 h-9 rounded-full bg-white border border-slate-200 flex items-center justify-center tap disabled:opacity-40"
          >
            <ChevronRight size={18} />
          </button>
        </div>

        {/* Filter chips */}
        <div className="mt-3 flex gap-2">
          {FILTERS.map((f) => (
            <button
              key={f.v}
              onClick={() => setTipe(f.v)}
              data-testid={`filter-${f.v}`}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold tap ${
                tipe === f.v
                  ? "text-white"
                  : "bg-white border border-slate-200 text-slate-600"
              }`}
              style={{
                backgroundColor: tipe === f.v ? "#1F4E5F" : undefined,
              }}
            >
              {f.l}
            </button>
          ))}
      </div>
        {/* Search bar */}
        <div className="mt-3 relative">
          <input
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setSearchMode(e.target.value.trim().length > 0);
            }}
            placeholder="🔍 Cari transaksi... (mis. Almeera, Zarin)"
            className="w-full h-10 pl-4 pr-10 rounded-xl border border-slate-200 text-sm bg-white focus:outline-none focus:border-blue-400"
          />
          {search && (
            <button
              onClick={() => { setSearch(""); setSearchMode(false); }}
              className="absolute right-3 top-2.5 text-slate-400 font-bold text-sm"
            >✕</button>
          )}
        </div>
        {searchMode && (
          <div className="mt-2 text-xs font-semibold" style={{ color: "#2563EB" }}>
            Menampilkan semua transaksi yang mengandung "{search}"
          </div>
        )}
      </div>
      {/* List */}
      <div className="px-4 mt-1">
      {searchMode && items.length > 0 && (
  <div className="mb-3">
    <div className="bg-white rounded-xl p-3 border border-slate-100 flex justify-between items-center">
      <div className="text-xs font-semibold text-slate-600">{items.length} transaksi ditemukan</div>
      <div className="text-sm font-bold" style={{ color: "#EF4444" }}>
        Keluar: -{formatRupiahFull(items.filter(t => t.tipe === "pengeluaran").reduce((a, t) => a + t.nominal, 0))}
      </div>
    </div>
  </div>
)}
        {loading ? (
          <div className="text-xs text-slate-500 py-8 text-center">
            Memuat...
          </div>
        ) : grouped.length === 0 ? (
          <div className="bg-white rounded-2xl p-8 text-center text-xs text-slate-500 border border-slate-100">
            Tidak ada transaksi di {formatBulanID(bulan)}.
          </div>
        ) : (
          <div className="space-y-4" data-testid="transaksi-list">
            {grouped.map(({ tanggal, items: rows, masuk, keluar }) => (
              <div key={tanggal}>
                <div className="flex items-baseline justify-between mb-1.5 px-1">
                  <div className="text-xs font-bold text-slate-700">
                    {formatTanggalID(tanggal)}
                  </div>
                  <div className="text-[11px] font-semibold">
                    {masuk > 0 && (
                      <span style={{ color: "#375623" }}>
                        {formatRupiahSigned(masuk, "+")}
                      </span>
                    )}
                    {masuk > 0 && keluar > 0 && (
                      <span className="text-slate-400"> / </span>
                    )}
                    {keluar > 0 && (
                      <span style={{ color: "#C00000" }}>
                        {formatRupiahSigned(keluar, "-")}
                      </span>
                    )}
                  </div>
                </div>
                <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
                  {rows.map((t, i) => {
                    const isTransfer = t.tipe === "transfer";
                    return (
                    <div
                      key={t.id}
                      className={`px-3 py-2.5 ${
                        i > 0 ? "border-t border-slate-100" : ""
                      }`}
                      style={isTransfer ? { backgroundColor: "#DEEAF1" } : undefined}
                      data-testid={`trx-row-${t.id}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0 flex-1 flex items-start gap-2">
                          {isTransfer && (
                            <div
                              className="mt-0.5 w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                              style={{ backgroundColor: "#2E75B6", color: "white" }}
                            >
                              <ArrowLeftRight size={11} strokeWidth={2.4} />
                            </div>
                          )}
                          <div className="min-w-0">
                            <div className="text-xs font-semibold text-slate-900 truncate">
                              {t.deskripsi}
                            </div>
                            <div className="text-[10px] text-slate-500 truncate">
                              {isTransfer
                                ? `${t.rekening_asal_nama} → ${t.rekening_tujuan_nama}`
                                : t.rekening_nama}
                              {!isTransfer && t.sumber ? ` · ${t.sumber}` : ""}
                            </div>
                          </div>
                        </div>
                        <div
                          className="text-xs font-bold whitespace-nowrap"
                          style={{
                            color: isTransfer
                              ? "#1F4E5F"
                              : t.tipe === "pemasukan"
                              ? "#375623"
                              : "#C00000",
                          }}
                        >
                          {isTransfer
                            ? formatRupiahFull(t.nominal)
                            : `${t.tipe === "pemasukan" ? "+" : "-"}${formatRupiahFull(t.nominal)}`}
                        </div>
                      </div>
                      <div className="flex gap-1 mt-1.5">
                        <button
                          onClick={() => {
                            setEditing(t);
                            setFormOpen(true);
                          }}
                          data-testid={`trx-edit-${t.id}`}
                          className="text-[10px] px-2 py-1 rounded-md bg-slate-100 text-slate-600 font-semibold tap flex items-center gap-1"
                        >
                          <Edit2 size={10} /> Edit
                        </button>
                        <button
                          onClick={() => setConfirmDel(t)}
                          data-testid={`trx-delete-${t.id}`}
                          className="text-[10px] px-2 py-1 rounded-md font-semibold tap flex items-center gap-1"
                          style={{ backgroundColor: "#FCE4E4", color: "#C00000" }}
                        >
                          <Trash2 size={10} /> Hapus
                        </button>
                      </div>
                    </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* FAB */}
      <button
        onClick={() => {
          setEditing(null);
          setFormOpen(true);
        }}
        data-testid="tambah-transaksi-fab"
        className="fixed left-1/2 -translate-x-[50%] w-full max-w-[380px] px-4"
        style={{ bottom: "80px" }}
      >
        <div
          className="w-full h-12 rounded-xl flex items-center justify-center gap-2 text-white font-semibold text-sm shadow-lg tap"
          style={{ backgroundColor: "#1F4E5F" }}
        >
          <Plus size={16} /> Tambah Transaksi
        </div>
      </button>

      <TransaksiForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        rekeningList={rekening}
        existing={editing}
        onSaved={load}
      />
      <DeleteConfirm
        open={!!confirmDel}
        onCancel={() => setConfirmDel(null)}
        onConfirm={doDelete}
        itemName={confirmDel?.deskripsi}
        testId="delete-trx-dialog"
      />
    </div>
  );
}
